import supabase from '../supabase/supabase';

const AGING_BUCKETS = ['current', '1-30', '31-60', '91+'];

const createAgingBuckets = () => ({
  current: 0,
  '1-30': 0,
  '31-60': 0,
  '61-90': 0,
  '91+': 0
});

const getAgingBucket = (dueDate, asOfDate) => {
  const due = new Date(`${dueDate}T00:00:00`);
  const asOf = new Date(`${asOfDate}T00:00:00`);
  const daysOverdue = Math.floor((asOf - due) / 86400000);

  if (daysOverdue <= 0) return 'current';
  if (daysOverdue <= 30) return '1-30';
  if (daysOverdue <= 60) return '31-60';
  if (daysOverdue <= 90) return '61-90';
  return '91+';
};

const isCostOfGoodsSold = (account) => {
  const type = String(account.type || '').trim().toLowerCase();
  const subType = String(account.sub_type || '').trim().toLowerCase();
  const name = String(account.name || '').trim().toLowerCase();

  return (
    type === 'cost of goods sold' ||
    type === 'cost of sales' ||
    subType.includes('cost of goods sold') ||
    subType.includes('cost of sales') ||
    subType === 'cogs' ||
    name === 'cost of goods sold' ||
    name === 'cost of sales' ||
    name === 'cogs'
  );
};

const isIncomeAccount = (account) => {
  const type = String(account.type || '').trim().toLowerCase();
  return type === 'revenue' || type === 'income';
};

const createAccountDetail = (account) => ({
  id: account.id,
  name: account.name,
  code: account.code || '',
  amount: 0,
  transactions: []
});

export const reportService = {
  /**
   * Helper to get common data (account_id, chart of accounts)
   * Supports passing accountId directly from the UI to ensure isolation.
   */
  async getBaseData(providedAccountId) {
    let accountId = providedAccountId;

    if (!accountId) {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data: membership, error: membershipError } = await supabase
        .schema('accounting')
        .from('organization_users')
        .select('organization_id')
        .eq('user_id', user.id)
        .order('created_at', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (membershipError) throw membershipError;

      if (!membership?.organization_id) {
        throw new Error('Your accounting workspace could not be loaded. Please refresh and try again.');
      }

      accountId = membership.organization_id;
    }

    const { data: coa, error: coaError } = await supabase
      .schema('accounting')
      .from('accounts')
      .select('*')
      .eq('organization_id', accountId);

    if (coaError) throw coaError;

    return { account_id: accountId, coa };
  },

  /**
   * Generates Profit & Loss Statement
   */
  async generatePL(startDate, endDate, accountId) {
    const { account_id, coa } = await this.getBaseData(accountId);

    const { data: transactions, error: txError } = await supabase
      .schema('accounting')
      .from('transactions')
      .select('*')
      .eq('organization_id', account_id)
      .gte('date', startDate)
      .lte('date', endDate);

    if (txError) throw txError;

    const report = {
      income: { total: 0, accounts: {} },
      expenses: { total: 0, accounts: {} },
      netIncome: 0,
      costOfGoodsSold: { total: 0, accounts: {} },
      grossProfit: 0,
      accountDetails: {
        income: [],
        costOfGoodsSold: [],
        expenses: []
      }
    };

    const detailsById = new Map();

    (coa || [])
      .filter((account) => account.is_active !== false)
      .forEach((account) => {
        const type = String(account.type || '').trim().toLowerCase();
        const detail = createAccountDetail(account);

        if (isIncomeAccount(account)) {
          detailsById.set(account.id, {
            section: 'income',
            detail
          });
        } else if (isCostOfGoodsSold(account)) {
          detailsById.set(account.id, {
            section: 'costOfGoodsSold',
            detail
          });
        } else if (type === 'expense') {
          detailsById.set(account.id, {
            section: 'expenses',
            detail
          });
        }
      });

    (transactions || []).forEach(tx => {
      const account = coa.find(a => a.id === tx.account_id);
      const accountDetail = detailsById.get(tx.account_id);

      if (!account || !accountDetail) return;

      const debit = Number(tx.debit || 0);
      const credit = Number(tx.credit || 0);
      const amount = isIncomeAccount(account)
        ? credit - debit
        : debit - credit;

      accountDetail.detail.amount += amount;
      accountDetail.detail.transactions.push({
        id: tx.id,
        date: tx.date,
        description: tx.description || '',
        reference: tx.reference_id || tx.reference_type || '',
        debit,
        credit,
        amount,
        status: tx.status || 'Cleared',
        payee: tx.payee || ''
      });

      if (accountDetail.section === 'income') {
        report.income.total += amount;
      } else if (accountDetail.section === 'costOfGoodsSold') {
        report.costOfGoodsSold.total += amount;
      } else {
        report.expenses.total += amount;
      }
    });

    detailsById.forEach(({ section, detail }) => {
      detail.amount = Number(detail.amount.toFixed(2));
      detail.transactions.sort((a, b) => (
        `${b.date}-${b.id}`.localeCompare(`${a.date}-${a.id}`)
      ));
      report.accountDetails[section].push(detail);

      if (detail.amount !== 0) {
        report[section].accounts[detail.name] = detail.amount;
      }
    });

    Object.values(report.accountDetails).forEach((accounts) => {
      accounts.sort((a, b) => a.name.localeCompare(b.name));
    });

    report.income.total = Number(report.income.total.toFixed(2));
    report.costOfGoodsSold.total = Number(report.costOfGoodsSold.total.toFixed(2));
    report.expenses.total = Number(report.expenses.total.toFixed(2));
    report.grossProfit = Number(
      (report.income.total - report.costOfGoodsSold.total).toFixed(2)
    );
    report.netIncome = Number(
      (report.grossProfit - report.expenses.total).toFixed(2)
    );

    return report;
  },

  /**
   * Generates Balance Sheet
   */
  async generateBalanceSheet(asOfDate, accountId, basis = 'accrual') {
    const { account_id, coa } = await this.getBaseData(accountId);

    let query = supabase
      .schema('accounting')
      .from('transactions')
      .select('*')
      .eq('organization_id', account_id)
      .lte('date', asOfDate)
      .order('date', { ascending: true })
      .order('created_at', { ascending: true });

    if (basis === 'cash') {
      query = query.in('status', ['Cleared', 'Reconciled']);
    }

    const { data: transactions, error: txError } = await query;

    if (txError) throw txError;

    const report = {
      assets: { total: 0, categories: {} },
      liabilities: { total: 0, categories: {} },
      equity: { total: 0, categories: {} },
      overview: {
        cashAndBank: 0,
        accountsReceivable: 0,
        liabilities: 0,
        netAssets: 0
      }
    };

    const getGroup = (account) => {
      const type = String(account.type || '').toLowerCase();
      const text = `${account.name || ''} ${account.sub_type || ''}`.toLowerCase();

      if (type === 'asset') {
        if (/(cash|bank|checking|savings|petty cash)/.test(text)) return 'Cash and Bank';
        if (/receivable/.test(text)) return 'Accounts Receivable';
        if (/(equipment|furniture|vehicle|building|fixed asset)/.test(text)) return 'Fixed Assets';
        if (/(investment|long.term|long term)/.test(text)) return 'Long-Term Assets';
        return 'Other Current Assets';
      }

      if (type === 'liability') {
        if (/payable/.test(text)) return 'Accounts Payable';
        if (/credit.?card/.test(text)) return 'Credit Cards';
        if (/(loan|note payable|mortgage|long.term|long term)/.test(text)) return 'Long-Term Liabilities';
        return 'Current Liabilities';
      }

      return 'Equity';
    };

    const addDetail = (section, group, account, balance, accountTransactions) => {
      if (!report[section].categories[group]) {
        report[section].categories[group] = {
          total: 0,
          accounts: {},
          accountDetails: []
        };
      }

      const normalDebit = section === 'assets';
      let runningBalance = 0;
      const detailTransactions = accountTransactions.map((transaction) => {
        const debit = Number(transaction.debit || 0);
        const credit = Number(transaction.credit || 0);
        runningBalance += normalDebit ? debit - credit : credit - debit;

        return {
          id: transaction.id,
          date: transaction.date,
          description: transaction.description || transaction.memo || '',
          reference: transaction.reference_type || transaction.reference_id || '—',
          debit,
          credit,
          runningBalance: Number(runningBalance.toFixed(2))
        };
      });

      const detail = {
        id: account.id,
        code: account.code || '',
        name: account.name,
        amount: balance,
        transactions: detailTransactions
      };

      report[section].categories[group].accounts[account.name] = balance;
      report[section].categories[group].accountDetails.push(detail);
      report[section].categories[group].total += balance;
      report[section].total += balance;
    };

    (coa || [])
      .filter((account) => account.is_active !== false)
      .forEach((account) => {
        const type = String(account.type || '').toLowerCase();
        if (!['asset', 'liability', 'equity'].includes(type)) return;

        const accountTransactions = (transactions || []).filter(
          (transaction) => transaction.account_id === account.id
        );
        const totalDebit = accountTransactions.reduce(
          (sum, transaction) => sum + Number(transaction.debit || 0),
          0
        );
        const totalCredit = accountTransactions.reduce(
          (sum, transaction) => sum + Number(transaction.credit || 0),
          0
        );
        const balance = type === 'asset'
          ? totalDebit - totalCredit
          : totalCredit - totalDebit;
        const section = type === 'asset'
          ? 'assets'
          : type === 'liability'
            ? 'liabilities'
            : 'equity';

        addDetail(section, getGroup(account), account, Number(balance.toFixed(2)), accountTransactions);
      });

    const currentYear = new Date(`${asOfDate}T00:00:00`).getFullYear();
    const currentYearStart = `${currentYear}-01-01`;
    const priorYearEnd = `${currentYear - 1}-12-31`;
    const calculateEarnings = (startDate, endDate) => (transactions || [])
      .filter((transaction) => transaction.date >= startDate && transaction.date <= endDate)
      .reduce((total, transaction) => {
        const account = (coa || []).find((item) => item.id === transaction.account_id);
        if (!account) return total;

        const type = String(account.type || '').toLowerCase();
        const debit = Number(transaction.debit || 0);
        const credit = Number(transaction.credit || 0);

        if (type === 'income' || type === 'revenue') return total + credit - debit;
        if (type === 'expense' || isCostOfGoodsSold(account)) return total - (debit - credit);
        return total;
      }, 0);

    const addCalculatedEquity = (name, amount) => {
      if (!report.equity.categories[name]) {
        report.equity.categories[name] = {
          total: 0,
          accounts: {},
          accountDetails: []
        };
      }

      report.equity.categories[name].accounts[name] = amount;
      report.equity.categories[name].accountDetails.push({
        id: `calculated-${name.toLowerCase().replace(/\s+/g, '-')}`,
        code: '',
        name,
        amount,
        transactions: []
      });
      report.equity.categories[name].total += amount;
      report.equity.total += amount;
    };

    addCalculatedEquity('Retained Earnings', Number(calculateEarnings('1900-01-01', priorYearEnd).toFixed(2)));
    addCalculatedEquity('Current Year Earnings', Number(calculateEarnings(currentYearStart, asOfDate).toFixed(2)));

    Object.values(report.assets.categories).forEach((category) => {
      category.total = Number(category.total.toFixed(2));
    });
    Object.values(report.liabilities.categories).forEach((category) => {
      category.total = Number(category.total.toFixed(2));
    });
    Object.values(report.equity.categories).forEach((category) => {
      category.total = Number(category.total.toFixed(2));
    });

    report.assets.total = Number(report.assets.total.toFixed(2));
    report.liabilities.total = Number(report.liabilities.total.toFixed(2));
    report.equity.total = Number(report.equity.total.toFixed(2));
    report.overview.cashAndBank = Number((report.assets.categories['Cash and Bank']?.total || 0).toFixed(2));
    report.overview.accountsReceivable = Number((report.assets.categories['Accounts Receivable']?.total || 0).toFixed(2));
    report.overview.liabilities = report.liabilities.total;
    report.overview.netAssets = Number((report.assets.total - report.liabilities.total).toFixed(2));

    return report;
  },

  /**
   * Generates Statement of Cash Flows
   */
  async generateCashFlow(startDate, endDate, accountId) {
    const { account_id, coa } = await this.getBaseData(accountId);
    const pl = await this.generatePL(startDate, endDate, account_id);
    const prevDate = new Date(new Date(startDate).getTime() - 86400000).toISOString().split('T')[0];
    const bsStart = await this.generateBalanceSheet(prevDate, account_id);
    const bsEnd = await this.generateBalanceSheet(endDate, account_id);

    const report = {
      operating: { total: 0, netIncome: pl.netIncome },
      investing: { total: 0 },
      financing: { total: 0 },
      beginningCash: 0,
      endingCash: 0,
      netChange: 0
    };

    const getBalance = (bs, accountName) => {
      for (const section of ['assets', 'liabilities', 'equity']) {
        for (const cat of Object.values(bs[section].categories)) {
          if (cat.accounts[accountName] !== undefined) return cat.accounts[accountName];
        }
      }
      return 0;
    };

    const getCashTotal = (bs) => {
      const bankCat = bs.assets.categories['Bank'] || bs.assets.categories['Cash'] || { total: 0 };
      return bankCat.total;
    };

    report.beginningCash = getCashTotal(bsStart);
    report.endingCash = getCashTotal(bsEnd);

    coa.forEach(acc => {
      const startBal = getBalance(bsStart, acc.name);
      const endBal = getBalance(bsEnd, acc.name);
      const diff = endBal - startBal;
      if (diff === 0) return;

      const type = acc.type.toLowerCase();
      const subType = (acc.sub_type || '').toLowerCase();

      if (subType.includes('receivable') || subType.includes('inventory') || subType.includes('prepaid')) {
        const effect = -diff;
        report.operating[acc.name] = effect;
        report.operating.total += effect;
      } else if (type === 'liability' && (subType.includes('payable') || subType.includes('accrued'))) {
        const effect = diff;
        report.operating[acc.name] = effect;
        report.operating.total += effect;
      } else if (subType.includes('fixed asset') || type === 'asset' && subType.includes('investment')) {
        const effect = -diff;
        report.investing[acc.name] = effect;
        report.investing.total += effect;
      } else if (type === 'liability' && subType.includes('loan') || type === 'equity' && acc.name !== 'Net Income / (Loss)') {
        const effect = diff;
        report.financing[acc.name] = effect;
        report.financing.total += effect;
      }
    });

    report.operating.total += pl.netIncome;
    report.netChange = report.operating.total + report.investing.total + report.financing.total;

    return report;
  },

  /**
   * Generates Accounts Receivable Aging from open invoices in Supabase.
   */
  async generateARAging(asOfDate, accountId) {
    const { account_id } = await this.getBaseData(accountId);

    const { data: invoices, error } = await supabase
      .schema('accounting')
      .from('invoices_20240522')
      .select(`
        id,
        invoice_number,
        issue_date,
        due_date,
        amount,
        status,
        customer:customer_id ( id, name )
      `)
      .eq('account_id', account_id)
      .lte('issue_date', asOfDate)
      .not('status', 'in', '("Paid","Void","Cancelled","Canceled")')
      .order('due_date', { ascending: true });

    if (error) throw error;

    const customersById = new Map();

    (invoices || []).forEach(invoice => {
      const amount = Number(invoice.amount || 0);
      if (amount <= 0) return;

      const customerId = invoice.customer?.id || invoice.customer_id || `unassigned-${invoice.id}`;
      const customerName = invoice.customer?.name || 'Unassigned customer';
      const dueDate = invoice.due_date || invoice.issue_date;
      const bucket = getAgingBucket(dueDate, asOfDate);

      if (!customersById.has(customerId)) {
        customersById.set(customerId, {
          id: customerId,
          name: customerName,
          invoices: [],
          buckets: createAgingBuckets(),
          total: 0
        });
      }

      const customer = customersById.get(customerId);
      customer.invoices.push({
        id: invoice.id,
        number: invoice.invoice_number || '—',
        issueDate: invoice.issue_date,
        dueDate,
        balance: amount,
        bucket,
        status: invoice.status || 'Draft'
      });
      customer.buckets[bucket] += amount;
      customer.total += amount;
    });

    const customers = Array.from(customersById.values())
      .map(customer => ({
        ...customer,
        invoices: customer.invoices.sort((a, b) => a.dueDate.localeCompare(b.dueDate))
      }))
      .sort((a, b) => b.total - a.total);

    const grandTotals = {
      buckets: createAgingBuckets(),
      total: 0
    };

    customers.forEach(customer => {
      AGING_BUCKETS.forEach(bucket => {
        grandTotals.buckets[bucket] += customer.buckets[bucket];
      });
      grandTotals.total += customer.total;
    });

    return { customers, grandTotals };
  },

  /**
   * Generates Accounts Payable Aging from unpaid bills in Supabase.
   */
  async generateAPAging(asOfDate, accountId) {
    const { account_id } = await this.getBaseData(accountId);

    const { data: bills, error } = await supabase
      .schema('accounting')
      .from('bills_20240522')
      .select(`
        id,
        bill_number,
        issue_date,
        due_date,
        amount,
        status,
        vendor:vendor_id ( id, name )
      `)
      .eq('account_id', account_id)
      .lte('issue_date', asOfDate)
      .not('status', 'in', '("Paid","Void","Cancelled","Canceled")')
      .order('due_date', { ascending: true });

    if (error) throw error;

    const vendorsById = new Map();

    (bills || []).forEach(bill => {
      const amount = Number(bill.amount || 0);
      if (amount <= 0) return;

      const vendorId = bill.vendor?.id || bill.vendor_id || `unassigned-${bill.id}`;
      const vendorName = bill.vendor?.name || 'Unassigned vendor';
      const dueDate = bill.due_date || bill.issue_date;
      const bucket = getAgingBucket(dueDate, asOfDate);

      if (!vendorsById.has(vendorId)) {
        vendorsById.set(vendorId, {
          id: vendorId,
          name: vendorName,
          bills: [],
          buckets: createAgingBuckets(),
          total: 0
        });
      }

      const vendor = vendorsById.get(vendorId);
      vendor.bills.push({
        id: bill.id,
        number: bill.bill_number || '—',
        issueDate: bill.issue_date,
        dueDate,
        amount,
        bucket,
        status: bill.status || 'Unpaid'
      });
      vendor.buckets[bucket] += amount;
      vendor.total += amount;
    });

    const vendors = Array.from(vendorsById.values())
      .map(vendor => ({
        ...vendor,
        bills: vendor.bills.sort((a, b) => a.dueDate.localeCompare(b.dueDate))
      }))
      .sort((a, b) => b.total - a.total);

    const grandTotals = {
      buckets: createAgingBuckets(),
      total: 0
    };

    vendors.forEach(vendor => {
      AGING_BUCKETS.forEach(bucket => {
        grandTotals.buckets[bucket] += vendor.buckets[bucket];
      });
      grandTotals.total += vendor.total;
    });

    return { vendors, grandTotals };
  }
};